import { useNavigate, useParams, useLocation } from "react-router-dom";
import MeaPage from "../../../components/MeaPage";
import ContractDetailHeader from "./components/ContractDetailHeader";
import contractsAPI from "../../../services/contracts";
import MeaPageTitle from "../../../components/MeaPageTitle";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import UpdateContractTotalAmountModal from "./modals/UpdateContractTotalAmountModal";
import MeaLink from "../../../components/MeaLink";
import MeaMemberAvatar from "../../../components/MeaMemberAvatar";
import CreateContractPaymentModal from "./modals/CreateContractPaymentModal";
import paymentsAPI from "../../../services/payments";
import MeaButton from "../../../components/MeaButton";
import { PERMISSION_TRAINING_CLASSES, PERMISSION_TRAINING_CONTRACTS, PERMISSION_TRAINING_LEVELS, PERMISSION_SCHOOL_MEMBERS, PERMISSION_TRAINING_PAYMENTS, PERMISSION_TRAINING_STUDENTS } from "../../../permissions";
import UpdateContractPaymentModal from "./modals/UpdateContractPaymentModal";
import uiDataAPI from "../../../services/ui_data";
import { ToFormatedDateForEvents } from "../../../utils/dates";
import { Button } from "primereact/button";
import MeaStudentStatus2 from "../../../components/MeaStudentStatus2";
import MeaStudentAvatar from "../../trainings/components/MeaStudentAvatar";
import { useAuthContext } from "../../../hooks/useAuthContext";
import AddSiblingsModal from "./modals/AddSiblingsModal";
import ContractActivitiesHistoryModal from "./modals/ContractActivitiesHistoryModal";
import SecretariatWorkspaceBar from "../../secretariat/components/SecretariatWorkspaceBar";
import { DataView } from "primereact/dataview";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import SendPaymentSituationModal from "./modals/SendPaymentSituationModal";


const ContractDetailPage = () => {
    const { hasPermission } = useAuthContext();
    //
    const params = useParams();
    const location = useLocation();
    const contractId = params.contractId;
    const schoolId = params.schoolId;
    const trainingId = params.registrationId ?? params.trainingId;
    const isSecretariatRoute = location.pathname.includes("/secretariat/");
    const accessToken = "";
    const [payment, setPayment] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState([]);
    //
    const toast = useRef();
    const navigate = useNavigate();
    //
    //
    const createPaymentModalRef = useRef(null);
    const updatePaymentModalRef = useRef(null);
    const updateContractTotalAmountModalRef = useRef(null);
    const addSiblingsModalRef = useRef(null);
    const contractActivitiesHistoryModalRef = useRef(null);
    const sendPaymentSituationModalRef = useRef(null);
    //
    const contractQuery = contractsAPI.GetOne(
        schoolId,
        trainingId,
        accessToken,
        contractId
    );
    const contractObj = contractQuery?.data?.data;
    // 
    const paymentsQuery = paymentsAPI.useGetList(
        schoolId,
        trainingId,
        accessToken,
        1,
        9999,
        { contract_id: contractId }
    );

    //
    const UiDataPaymentMethodTypesListQuery = uiDataAPI.UiDataPaymentMethodTypesList(
        accessToken
    );
    const UiDataPaymentStatusTypesListQuery = uiDataAPI.UiDataPaymentStatusTypesList(
        accessToken
    );

    // Function to remove selected students from contract
    const handleRemoveStudents = () => {
        if (selectedStudents.length === 0) {
            toast.current.show({
                severity: "warn",
                summary: "Attention",
                detail: "Veuillez sélectionner au moins un élève à retirer",
                life: 3000,
            });
            return;
        }

        confirmDialog({
            message: `Voulez-vous vraiment retirer ${selectedStudents.length} élève(s) de ce contrat?`,
            header: "Confirmation de retrait",
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    // Extract only the student IDs from the selectedStudents array
                    const studentIdsToRemove = selectedStudents.map(student => student.id);
                    
                    // Call the service to update contract students
                    // When action is "REMOVE", we send the IDs of students to remove
                    await contractsAPI.UpdateContractStudents(
                        schoolId, 
                        trainingId, 
                        accessToken, 
                        contractId, 
                        { 
                            action: "REMOVE",
                            students_ids: studentIdsToRemove 
                        }
                    );
                    
                    // Clear selection and refresh data
                    setSelectedStudents([]);
                    contractQuery.refetch();
                    
                    toast.current.show({
                        severity: "success",
                        summary: "Succès",
                        detail: `${selectedStudents.length} élève(s) retiré(s) du contrat avec succès!`,
                        life: 3000,
                    });
                } catch (error) {
                    console.error("Error removing students:", error);
                    toast.current.show({
                        severity: "error",
                        summary: "Erreur",
                        detail: "Erreur lors du retrait des élèves du contrat",
                        life: 3000,
                    });
                }
            },
        });
    };

    //
    const handleSendPaymentSituationEmail = () => {
        if (!contractId) return;
        sendPaymentSituationModalRef.current.show(contractId);
    };

    //
    const startToolbarTemplate = (
        <div>
            <MeaButton permission={PERMISSION_TRAINING_PAYMENTS.CREATE} label="Ajouter un paiement" icon="pi pi-plus"
                className="p-button mr-2 action"

                onClick={() => {
                    createPaymentModalRef.current.show(() => {
                        contractQuery.refetch();
                        paymentsQuery.refetch();
                    });
                }}
            />
            <MeaButton
                permission={PERMISSION_TRAINING_PAYMENTS.GET}
                label="Envoyer un mail de situation"
                icon="pi pi-envelope"
                className="p-button p-button-outlined mr-2"
                onClick={handleSendPaymentSituationEmail}
            />
        </div>
    );

    //
    const breadcrumb = isSecretariatRoute
        ? <SecretariatWorkspaceBar section="payments" trailing={`Contrat ${contractObj?.id ?? ""}`.trim()} />
        : undefined;

    const paymentCardStyle = {
        background: "linear-gradient(145deg, #f6f8ff 0%, #ffffff 100%)",
        border: "1px solid rgba(99, 102, 241, 0.12)",
        boxShadow: "0 18px 32px -20px rgba(37, 99, 235, 0.45)",
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
    };

    const getMethodIcon = (methodId) => {
        if (!methodId) {
            return "pi pi-wallet";
        }
        const mapping = {
            CASH: "pi pi-euro",
            CB: "pi pi-credit-card",
            CHEQUE: "pi pi-wallet",
            OTHER: "pi pi-align-justify",
        };
        return mapping[methodId] || "pi pi-wallet";
    };

    const getStatusMeta = (statusName) => {
        if (!statusName) {
            return {
                severity: "info",
                icon: "pi pi-info-circle",
                className: "bg-blue-50 text-blue-700 border-blue-100",
            };
        }

        const key = statusName.trim().toLowerCase();
        const mapping = {
            "encaissé": {
                severity: "success",
                icon: "pi pi-check-circle",
                className: "bg-green-50 text-green-700 border-green-100",
                gradient: "linear-gradient(90deg, rgba(34,197,94,0.9) 0%, rgba(22,163,74,0.9) 100%)",
            },
            "en attente d'encaissement": {
                severity: "warning",
                icon: "pi pi-hourglass",
                className: "bg-orange-50 text-orange-700 border-orange-100",
                gradient: "linear-gradient(90deg, rgba(251,191,36,0.9) 0%, rgba(245,158,11,0.9) 100%)",
            },
        };

        return (
            mapping[key] || {
                severity: "info",
                icon: "pi pi-info-circle",
                className: "bg-blue-50 text-blue-700 border-blue-100",
                gradient: "linear-gradient(90deg, rgba(129,140,248,0.8) 0%, rgba(56,189,248,0.8) 100%)",
            }
        );
    };

    return (
        <>
            <CreateContractPaymentModal ref={createPaymentModalRef} contractId={contractId} schoolId={schoolId} trainingId={trainingId} />
            <UpdateContractPaymentModal ref={updatePaymentModalRef} payment={payment} contractId={contractId} schoolId={schoolId} trainingId={trainingId} />
            <UpdateContractTotalAmountModal ref={updateContractTotalAmountModalRef} contract={contractObj} schoolId={schoolId} trainingId={trainingId} />
            <AddSiblingsModal 
                ref={addSiblingsModalRef} 
                contractId={contractId} 
                schoolId={schoolId} 
                trainingId={trainingId}
            />
            <ContractActivitiesHistoryModal
                ref={contractActivitiesHistoryModalRef}
                contractId={contractId}
                schoolId={schoolId}
                trainingId={trainingId}
            />
            <SendPaymentSituationModal
                ref={sendPaymentSituationModalRef}
                schoolId={schoolId}
                trainingId={trainingId}
                accessToken={accessToken}
                toast={toast}
            />
            <MeaPage
                query={contractQuery}
                title={<MeaPageTitle prefix={"Contrat:"} title={contractObj?.id} />}
                breadcrumb={breadcrumb}
                showBackButton={true}
                actions={[
                    {
                        label: "Afficher l'historique des activités",
                        icon: "pi pi-history",
                        disable: !hasPermission(PERMISSION_TRAINING_CONTRACTS.GET),
                        command: () => {
                            if (contractActivitiesHistoryModalRef.current) {
                                contractActivitiesHistoryModalRef.current.show();
                            }
                        },
                    },
                    {
                        label: "Supprimer",
                        icon: "pi pi-trash",
                        disable: !hasPermission(PERMISSION_TRAINING_CONTRACTS.DELETE),
                        command: () => {
                            confirmDialog({
                                message: "Voulez-vous vraiment supprimer ce contrat?",
                                header: "Confirmation",
                                icon: "pi pi-info-circle",
                                defaultFocus: "reject",
                                acceptClassName: "p-button-danger",
                                accept: () => {
                                    contractsAPI
                                        .DeleteOne(schoolId, trainingId, accessToken, contractObj?.id)
                                        .then((data) => {
                                            toast.current.show({
                                                severity: "success",
                                                summary: "Succès",
                                                detail: contractObj?.id + " Supprimé!",
                                                life: 3000,
                                            });
                                            const listPath = isSecretariatRoute
                                                ? "/school/" + schoolId + "/secretariat/payments/" + trainingId
                                                : "/school/" + schoolId + "/admin/registrations/" + trainingId + "/payments";
                                            navigate(listPath);
                                        })
                                        .catch((error) => {
                                            toast.current.show({
                                                severity: "error",
                                                summary: "Erreur",
                                                detail: "Erreur de suppression!",
                                            });
                                        });
                                },
                            });
                        },
                    },
                ]}
            >
                <ContractDetailHeader contract={contractObj} callback={() => {
                    contractQuery.refetch();
                }} schoolId={schoolId} trainingId={trainingId} />
                <Toast ref={toast} />
                <ConfirmDialog />
                <div className="mt-5">
                    <div className="grid">
                        
                        <div className="col-12 md:col-7 ">
                            <div className="surface-section p-3 border-round-sm">
                                <div className="flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                                    <div className="font-bold text-xl text-blue-500">Paiements</div>
                                    {startToolbarTemplate}
                                </div>
                                {paymentsQuery.isLoading ? (
                                    <div className="flex justify-content-center py-6">
                                        <ProgressSpinner
                                            style={{ width: "50px", height: "50px" }}
                                            strokeWidth="8"
                                            fill="var(--surface-ground)"
                                            animationDuration=".5s"
                                        />
                                    </div>
                                ) : paymentsQuery.isError ? (
                                    <div className="p-4 text-center text-500 border-1 surface-border border-round-sm">
                                        Erreur lors du chargement des paiements.
                                    </div>
                                ) : (
                                    <>
                                        {(!paymentsQuery.data?.data || paymentsQuery.data.data.length === 0) ? (
                                            <div className="p-4 text-center text-500 border-1 surface-border border-round-sm">
                                                Aucun paiement enregistré pour ce contrat.
                                            </div>
                                        ) : (
                                            <DataView
                                                value={paymentsQuery.data?.data || []}
                                                layout="grid"
                                                emptyMessage="Aucun paiement."
                                                //contentClassName="flex flex-wrap gap-3 justify-content-start"
                                                itemTemplate={(item) => {
                                                    if (!item) {
                                                        return null;
                                                    }

                                                    const method = UiDataPaymentMethodTypesListQuery.data?.data?.find(
                                                        (method) => method.id === item?.method
                                                    );
                                                    const status = UiDataPaymentStatusTypesListQuery.data?.data?.find(
                                                        (status) => status.id === item?.status
                                                    );
                                                    const paymentReceivedDate = item?.payment_received_date
                                                        ? ToFormatedDateForEvents(new Date(item.payment_received_date), true)
                                                        : "-";
                                                    const isHelloAsso = item?.method === "HELLOASSO";
                                                    const paymentExternalId = isHelloAsso ? (item?.helloasso_payment_id || "-") : (item?.external_id || "-");
                                                    const hasMultipleInstallments = isHelloAsso && paymentsQuery.data?.data?.filter(
                                                        (p) => p.method === "HELLOASSO" && p.helloasso_order_id && p.helloasso_order_id === item.helloasso_order_id
                                                    ).length > 1;
                                                    const showInstallment = isHelloAsso && (item?.helloasso_installment_number > 1 || (item?.helloasso_installment_number > 0 && hasMultipleInstallments));
                                                    const paymentDate = item?.payment_date
                                                        ? ToFormatedDateForEvents(new Date(item.payment_date), true)
                                                        : "-";
                                                    const paymentRefundedDate = item?.payment_refund_date
                                                        ? ToFormatedDateForEvents(new Date(item.payment_refund_date), true)
                                                        : "-";
                                                    const amountFormatter = new Intl.NumberFormat("fr-FR", {
                                                        style: "currency",
                                                        currency: "EUR",
                                                        minimumFractionDigits: 2,
                                                    });
                                                    const formattedAmount = item?.amount != null
                                                        ? amountFormatter.format(Number(item.amount))
                                                        : "-";
                                                    const methodIconClass = getMethodIcon(method?.id);
                                                    const isPaidStatus = item?.status === "PAID";
                                                    const isRefundedStatus = item?.status === "REFUNDED";

                                                    return (
                                                        <div className="p-2" style={{ flex: "0 0 270px", maxWidth: "270px", width: "270px" }}>
                                                            <div
                                                                className="border-1 surface-border border-round-md p-3 h-full flex flex-column gap-1 surface-card"
                                                                style={paymentCardStyle}
                                                            >
                                                                <div
                                                                    style={{
                                                                        height: "6px",
                                                                        borderRadius: "9999px",
                                                                        background: (() => {
                                                                            const statusMeta = getStatusMeta(status?.name);
                                                                            if (statusMeta?.gradient) {
                                                                                return statusMeta.gradient;
                                                                            }
                                                                            return "linear-gradient(90deg, #6366f1 0%, #22d3ee 100%)";
                                                                        })(),
                                                                        margin: "-1rem -1rem 1rem",
                                                                    }}
                                                                />
                                                                <div className="flex flex-column gap-3" style={{ flexGrow: 1 }}>
                                                                    <div className="flex justify-content-between align-items-start gap-3">
                                                                        <div className="flex flex-column gap-2">
                                                                            <div className="flex align-items-center gap-2">
                                                                                <i className={`${methodIconClass} text-primary-500 text-lg`} aria-hidden />
                                                                                <span className="text-900 font-semibold text-lg" style={{ wordBreak: "break-word" }}>
                                                                                    {method?.name || "Méthode inconnue"}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex align-items-center gap-2 text-xl font-bold text-primary-600">
                                                                            <div className="flex align-items-center gap-2">                                                                                
                                                                                <span>{formattedAmount}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex align-items-start gap-2 text-500 text-sm break-word">
                                                                        <i className="pi pi-align-left mt-1" aria-hidden />
                                                                        <span style={{ wordBreak: "break-word" }}>{item?.details || "-"}</span>
                                                                    </div>
                                                                    <div className="grid text-sm text-600">
                                                                        <div className="col-12">
                                                                            <div className="flex align-items-center gap-2">
                                                                                <i className="pi pi-calendar-plus" aria-hidden />
                                                                                <span>
                                                                                    <span className="font-semibold text-700">Date de réception&nbsp;:</span>{" "}
                                                                                    <br />
                                                                                    <span>{paymentReceivedDate}</span>
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-12">
                                                                            <div className="flex align-items-center gap-2">
                                                                                <i className="pi pi-calendar" aria-hidden />
                                                                                <span>
                                                                                    <span className="font-semibold text-700">
                                                                                        {isPaidStatus ? "Date d'encaissement" : "Date d'encaissement prévue"}&nbsp;:
                                                                                    </span>{" "}
                                                                                    <br />
                                                                                    <span>{paymentDate}</span>
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        {isRefundedStatus && (
                                                                            <div className="col-12">
                                                                                <div className="flex align-items-center gap-2">
                                                                                    <i className="pi pi-replay" aria-hidden />
                                                                                    <span>
                                                                                        <span className="font-semibold text-700">Date de rembourcement&nbsp;:</span>{" "}
                                                                                        <br />
                                                                                        <span>{paymentRefundedDate}</span>
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isHelloAsso && (
                                                                            <div className="col-12">
                                                                                <div className="flex align-items-center gap-2">
                                                                                    <i className="pi pi-calendar-plus" aria-hidden />
                                                                                    <span>
                                                                                        <span className="font-semibold text-700">N° de paiement&nbsp;:</span>{" "}
                                                                                        <br />
                                                                                        <span>{paymentExternalId}</span>
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isHelloAsso && (
                                                                            <div className="col-12">
                                                                                <div className="flex align-items-center gap-2">
                                                                                    <i className="pi pi-hashtag" aria-hidden />
                                                                                    <span>
                                                                                        <span className="font-semibold text-700">N° d'ordre&nbsp;:</span>{" "}
                                                                                        <br />
                                                                                        <span>{item?.helloasso_order_id || "-"}</span>
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isHelloAsso && showInstallment && (
                                                                            <div className="col-12">
                                                                                <div className="flex align-items-center gap-2">
                                                                                    <i className="pi pi-sort-numeric-down" aria-hidden />
                                                                                    <span>
                                                                                        <span className="font-semibold text-700">N° d'échéance&nbsp;:</span>{" "}
                                                                                        <br />
                                                                                        <span>{item?.helloasso_installment_number || "-"}</span>
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isHelloAsso && (
                                                                            <div className="col-12">
                                                                                <div className="flex align-items-center gap-2">
                                                                                    <i className="pi pi-file" aria-hidden />
                                                                                    <span>
                                                                                        <span className="font-semibold text-700">Attestation de paiement&nbsp;:</span>{" "}
                                                                                        <br />
                                                                                        {item?.helloasso_payment_receipt_url ? (
                                                                                            <a
                                                                                                href={item.helloasso_payment_receipt_url}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                className="text-primary-600 hover:underline inline-flex align-items-center gap-1 font-medium"
                                                                                            >
                                                                                                Télécharger <i className="pi pi-external-link" style={{ fontSize: "0.75rem" }} />
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span>-</span>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex align-items-center justify-content-between gap-2 pt-2" style={{ marginTop: "auto" }}>
                                                                    <div className="flex align-items-center">
                                                                        {status?.name && (() => {
                                                                            const statusMeta = getStatusMeta(status.name);
                                                                            return (
                                                                                <Tag
                                                                                    value={status.name}
                                                                                    severity={statusMeta.severity}
                                                                                    icon={statusMeta.icon}
                                                                                    className={statusMeta.className}
                                                                                />
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        {hasPermission(PERMISSION_TRAINING_PAYMENTS.UPDATE) && (
                                                                            <MeaButton
                                                                                icon="pi pi-pencil"
                                                                                rounded
                                                                                text
                                                                                style={{ padding: "0", height: "1.8rem", width: "1.8rem" }}
                                                                                aria-label="Modifier"
                                                                                onClick={() => {
                                                                                    setPayment(item);
                                                                                    if (updatePaymentModalRef.current) {
                                                                                        updatePaymentModalRef.current.show(() => {
                                                                                            paymentsQuery.refetch();
                                                                                            contractQuery.refetch();
                                                                                        });
                                                                                    }
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {hasPermission(PERMISSION_TRAINING_PAYMENTS.DELETE) && (
                                                                            <MeaButton
                                                                                icon="pi pi-trash"
                                                                                rounded
                                                                                text
                                                                                severity="danger"
                                                                                style={{ padding: "0", height: "1.8rem", width: "1.8rem" }}
                                                                                aria-label="Supprimer"
                                                                                onClick={() => {
                                                                                    confirmDialog({
                                                                                        message: "Voulez-vous supprimer ce paiement ?",
                                                                                        header: "Confirmation",
                                                                                        icon: "pi pi-info-circle",
                                                                                        acceptClassName: "p-button-danger",
                                                                                        rejectClassName: "p-button-text",
                                                                                        accept: async () => {
                                                                                            try {
                                                                                                await paymentsAPI.DeleteOne(
                                                                                                    schoolId,
                                                                                                    trainingId,
                                                                                                    accessToken,
                                                                                                    item?.id
                                                                                                );
                                                                                                toast.current.show({
                                                                                                    severity: "success",
                                                                                                    summary: "Succès",
                                                                                                    detail: "Paiement supprimé avec succès.",
                                                                                                    life: 3000,
                                                                                                });
                                                                                                contractQuery.refetch();
                                                                                                paymentsQuery.refetch();
                                                                                            } catch (error) {
                                                                                                toast.current.show({
                                                                                                    severity: "error",
                                                                                                    summary: "Erreur",
                                                                                                    detail: "Erreur lors de la suppression du paiement.",
                                                                                                    life: 3000,
                                                                                                });
                                                                                            }
                                                                                        },
                                                                                    });
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="col-12 md:col-5 ">
                            <div className="grid">
                                <div className="col-12">
                                    <div className="surface-section  p-3 border-round-sm font-bold">
                                        <div className="font-bold text-xl text-blue-500 mb-3 flex justify-content-between align-items-center">
                                            <span>Elèves</span>
                                            <div className="flex gap-2">
                                                <MeaButton
                                                    label="Ajouter des frères/sœurs"
                                                    outlined
                                                    icon="pi pi-users"
                                                    onClick={() => {
                                                        addSiblingsModalRef.current.show(() => {
                                                            contractQuery.refetch();
                                                        });
                                                    }}
                                                    permission={PERMISSION_TRAINING_CONTRACTS.UPDATE}
                                                />
                                                {selectedStudents.length > 0 && (
                                                    <MeaButton
                                                        label={`Retirer ${selectedStudents.length} élève(s)`}
                                                        icon="pi pi-user-minus"
                                                        severity="danger"
                                                        size="small"
                                                        onClick={handleRemoveStudents}
                                                        permission={PERMISSION_TRAINING_CONTRACTS.UPDATE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <DataTable 
                                            value={contractObj?.students} 
                                            className="p-datatable-sm"
                                            selection={selectedStudents}
                                            onSelectionChange={(e) => setSelectedStudents(e.value)}
                                            dataKey="id"
                                        >
                                            <Column 
                                                selectionMode="multiple" 
                                                headerStyle={{ width: "3rem" }}
                                            />
                                            <Column field="id" header="Elève" body={(item) => {
                                                return <MeaStudentAvatar permission={PERMISSION_TRAINING_STUDENTS.GET} student={item} schoolId={schoolId} trainingId={trainingId} link="candidates" />
                                            }} />
                                            <Column field="level_id" header="Niveau" body={(item) => {
                                                return (<><MeaLink to={`/school/${schoolId}/admin/trainings/${trainingId}/levels/${item.level_id}`
                                                } permission={PERMISSION_TRAINING_LEVELS.GET}>{item.level?.name}</MeaLink></>)
                                            }} />
                                            <Column field="class_id" header="Classe" body={(item) => {
                                                return (<><MeaLink to={`/school/${schoolId}/admin/trainings/${trainingId}/classes/${item.class_id}`
                                                } permission={PERMISSION_TRAINING_CLASSES.GET}>{item.classe?.name}</MeaLink></>)
                                            }} />
                                            <Column field="class_id" header="Status" body={(item) => {
                                                return (<><MeaStudentStatus2 student={item} /></>)
                                            }} />
                                        </DataTable>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="surface-section  p-3 border-round-sm font-bold">
                                        <div className="font-bold text-xl text-blue-500 mb-3">Parents</div>
                                        <DataTable value={contractObj?.parents} className="p-datatable-sm">
                                            <Column field="id" header="Parent" body={(item) => {
                                                return <MeaMemberAvatar member={item} permission={PERMISSION_SCHOOL_MEMBERS.GET} schoolId={schoolId} trainingId={trainingId} />
                                            }} />
                                            <Column field="phone" header="Téléphone" body={(item) => {
                                                return <div className="w-full">{item?.phone && <Button size="small" icon="pi pi-phone" label={item?.phone || "-"} severity="secondary" rounded onClick={() => {
                                                    window.open("tel:" + item?.phone)
                                                }} />} </div>
                                            }} />
                                            <Column field="email" header="E-mail" />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </MeaPage>
        </>
    );
};
export default ContractDetailPage;
